import type { QuickApplyProfile, TrackingMetadata } from "./types";

const TIMEOUT_S = 600;

export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(`Unauthorized: ${message}`);
    this.name = "UnauthorizedError";
  }
}

export class QuickApplyClient {
  private url: string;
  private metadata: TrackingMetadata;
  private loginWindow: Window | null;

  constructor(url: string, metadata: TrackingMetadata) {
    this.url = url;
    this.metadata = metadata;
    this.loginWindow = null;
  }

  public async fetchProfile(): Promise<QuickApplyProfile> {
    this.validateUrl();

    const response = await fetch(`${this.url}/users/self?job_post_id=${this.metadata.jobPostId}`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status === 401) {
      throw new UnauthorizedError("User is not logged in.");
    }

    throw new Error(`Failed to fetch profile: ${response.status} ${response.statusText}`);
  }

  public initiateLoginFlow(): void {
    // We use an empty string for the URL so the window doesn't get focused.
    // We set the URL later, once we know the user is not logged in.
    this.loginWindow = window.open("", "_blank");
  }

  public endLoginFlow(): void {
    this.loginWindow?.close();
  }

  public fetchProfileAfterLogin(): Promise<QuickApplyProfile | null> {
    this.validateUrl();
    this.setUrlOnLoginWindow();

    return new Promise((resolve, reject) => {
      const onFocus = async () => {
        let keepListeningForFocus = false;
        try {
          const profile = await this.fetchProfile();
          resolve(profile);
        } catch (err) {
          if (err instanceof UnauthorizedError) {
            if (this.loginWindow?.closed) {
              resolve(null);
            } else {
              keepListeningForFocus = true;
            }
            return;
          }
          reject(err);
        } finally {
          if (!keepListeningForFocus) {
            cleanup();
          }
        }
      };

      const timeout = window.setTimeout(() => {
        cleanup();
        resolve(null);
      }, TIMEOUT_S * 1000);

      const cleanup = () => {
        window.clearTimeout(timeout);
        window.removeEventListener("focus", onFocus);
      };

      window.addEventListener("focus", onFocus);
    });
  }

  private validateUrl(): void {
    if (!this.url) {
      throw new Error("Quick Apply URL is not set.");
    }
  }

  private buildUrl(path: string, params: Record<string, string | number | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === "") {
        return;
      }
      searchParams.set(key, String(value));
    });
    const query = searchParams.toString();
    return query ? `${this.url}${path}?${query}` : `${this.url}${path}`;
  }

  private setUrlOnLoginWindow(): void {
    if (!this.loginWindow) {
      return;
    }

    this.loginWindow.location = this.buildUrl("/users/sign_in", {
      initiator: "autofill",
      source: "quick_apply",
      job_post_id: this.metadata.jobPostId,
      job_board: this.metadata.urlToken,
    });
  }
}
